import React, { useState } from 'react';
import {
  Play, Download, Share2, Settings, RefreshCw, BarChart2, Atom,
  ChevronDown, ChevronRight, HelpCircle, Zap, Target, Database, Sliders, Box, Layers, Clock
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../common/Button';
import { useSimulation } from '../../hooks/useSimulation';
import { getExperimentType } from '../../context/SimulationContext';
import { MATERIALS } from '../../data/physicsConstants';
import { presets as PRESETS_DATA } from '../../data/presets.json';

// ============================================
// TOOLTIP
// ============================================
const Tooltip = ({ children, content }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-block">
      <div onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)} className="cursor-help">
        {children}
      </div>
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 p-2 bg-slate-800 text-white text-xs rounded-lg shadow-xl"
          >
            {content}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// MATERIAL CHIP SELECTOR (Monochrome)
// ============================================
const MaterialChip = ({ material, matKey, isSelected, onSelect }) => (
  <button
    onClick={onSelect}
    className={`
      flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-sm font-medium w-full
      ${isSelected
        ? 'border-[#1d1d1f] bg-[#1d1d1f] text-white shadow-md'
        : 'border-gray-200 bg-white hover:border-gray-300 text-[#1d1d1f] hover:bg-gray-50'
      }
    `}
  >
    <span
      className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold ${isSelected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}
    >
      {matKey.slice(0, 2).toUpperCase()}
    </span>
    <div className="text-left flex-1">
      <div className="text-xs font-semibold">{material.name.split(' ')[0]}</div>
      <div className={`text-[10px] ${isSelected ? 'text-gray-300' : 'text-gray-500'}`}>X₀ = {material.X0} cm</div>
    </div>
  </button>
);

// ============================================
// SLIDER
// ============================================
const Slider = ({ label, value, min, max, step, unit, onChange, tooltip }) => (
  <div className="space-y-3">
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        {tooltip && (
          <Tooltip content={tooltip}>
            <HelpCircle size={13} className="text-gray-400" />
          </Tooltip>
        )}
      </div>
      <span className="font-mono text-xs font-bold text-[#1d1d1f] bg-gray-100 px-2 py-0.5 rounded">
        {typeof value === 'number' ? value.toFixed(step < 1 ? 2 : 0) : value} {unit}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full h-1 bg-gray-200 rounded-full appearance-none cursor-pointer accent-[#1d1d1f]"
    />
  </div>
);

// ============================================
// TOGGLE SWITCH
// ============================================
const Toggle = ({ label, value, onChange, tooltip }) => (
  <label className="flex items-center justify-between p-2 rounded-lg border border-slate-100 bg-white group relative">
    <div className="flex items-center gap-1">
      <span className="text-sm text-slate-700">{label}</span>
      {tooltip && (
        <Tooltip content={tooltip}>
          <HelpCircle size={12} className="text-slate-400" />
        </Tooltip>
      )}
    </div>
    <button
      onClick={() => onChange(!value)}
      className={`relative w-11 h-6 rounded-full p-1 transition-colors duration-300 ${value ? 'bg-[#1d1d1f]' : 'bg-gray-200'}`}
    >
      <motion.div
        className="w-4 h-4 bg-white rounded-full shadow"
        animate={{ x: value ? 20 : 0 }}
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
      />
    </button>
  </label>
);

// ============================================
// SECTION
// ============================================
const Section = ({ title, icon: Icon, children, defaultOpen = false, badge }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-slate-100">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon size={16} className="text-[#1d1d1f]" />}
          <span className="font-semibold text-sm text-gray-800">{title}</span>
          {badge && (
            <span className="text-[10px] bg-[#1d1d1f] text-white px-1.5 py-0.5 rounded font-medium">
              {badge}
            </span>
          )}
        </div>
        <motion.div animate={{ rotate: isOpen ? 90 : 0 }}>
          <ChevronRight size={14} className="text-gray-400" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-3">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// MCS QUICK STATS BAR
// ============================================
const QuickStats = ({ derived, material }) => {
  const mat = MATERIALS[material] || MATERIALS.pvc;
  return (
    <div className="grid grid-cols-3 gap-2 p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="text-center">
        <div className="text-[10px] text-gray-500 uppercase font-medium">β</div>
        <div className="text-xs font-mono font-bold text-[#1d1d1f]">{derived.beta.toFixed(4)}</div>
      </div>
      <div className="text-center border-x border-gray-100">
        <div className="text-[10px] text-gray-500 uppercase font-medium">θ_RMS</div>
        <div className="text-xs font-mono font-bold text-[#1d1d1f]">{derived.mcsAngle?.toFixed(2) || '-'} mrad</div>
      </div>
      <div className="text-center">
        <div className="text-[10px] text-gray-500 uppercase font-medium">X₀</div>
        <div className="text-xs font-mono font-bold text-[#1d1d1f]">{mat.X0} cm</div>
      </div>
    </div>
  );
};

// ============================================
// MAIN CONTROL PANEL
// ============================================
const ControlPanel = ({ onShowResults, onOpenControlRoom }) => {
  const { state, actions, runSimulation, downloadResults, isRunning, progress, results } = useSimulation();
  const experimentType = getExperimentType(state);
  
  // Find active preset data
  const activePresetData = state.activePreset
    ? PRESETS_DATA.find(p => p.id === state.activePreset)
    : null;

  const headerTitle = activePresetData ? activePresetData.name : "Custom Configuration";
  const headerSubtitle = activePresetData ? "Experiment Setup" : "Manual Setup";

  // Determine Visible Sections based on Experiment Type
  const showMaterial = ['mcs-tomography', 'medical', 'calorimeter', 'rutherford'].includes(experimentType);
  const showInsert = ['mcs-tomography', 'medical'].includes(experimentType);
  const showPhysics = true; // Always show physics options
  const showDetector = true; // Always show detector options

  return (
    <div className="h-full flex flex-col bg-white border-l border-slate-200 shadow-lg overflow-hidden">

      {/* Header */}
      <div className="p-4 bg-[#f5f5f7] border-b border-gray-200 text-[#1d1d1f]">
        <div className="flex items-center gap-3 mb-3">
          <Atom size={24} className="text-[#0071e3]" />
          <div>
            <h2 className="text-lg font-bold leading-tight">{headerTitle}</h2>
            <p className="text-xs text-gray-500">{headerSubtitle}</p>
          </div>
        </div>

        {/* Control Center Button */}
        {onOpenControlRoom && (
          <button
            onClick={onOpenControlRoom}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-gray-50 rounded-lg font-medium text-sm border border-gray-300 text-gray-700 transition-all shadow-sm"
          >
            <Database size={16} />
            Open Full Control Center
          </button>
        )}
      </div>

      {/* Active Configuration */}
      <div className="p-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#1d1d1f] text-white shadow-sm"
          >
            <Box size={20} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-[#1d1d1f] text-sm">{MATERIALS[state.material]?.name || 'PVC'}</div>
            <div className="text-xs text-gray-500">
              {state.beam.momentum} GeV/c • {state.phantomThickness} cm
            </div>
          </div>
        </div>
        <QuickStats derived={state.derived} material={state.material} />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">

        {/* Material Selection (Conditional) */}
        {showMaterial && (
          <Section title="Target Material" icon={Box} defaultOpen={true} badge={state.material?.toUpperCase()}>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(MATERIALS).filter(([k]) => k !== 'air').map(([key, material]) => (
                <MaterialChip
                  key={key}
                  matKey={key}
                  material={material}
                  isSelected={state.material === key}
                  onSelect={() => actions.setMaterial(key)}
                />
              ))}
            </div>
          </Section>
        )}

        {/* Insert Material (Conditional) */}
        {showInsert && (
          <Section title="Insert Material" icon={Layers} defaultOpen={false} badge={state.insertMaterial || 'NONE'}>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => actions.setInsertMaterial(null)}
                className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all
                  ${!state.insertMaterial
                    ? 'border-[#0033A0] bg-blue-50 text-[#0033A0]'
                    : 'border-slate-200 bg-white text-slate-500'}`}
              >
                None
              </button>
              {Object.entries(MATERIALS)
                .filter(([k]) => k !== 'air' && k !== state.material)
                .slice(0, 3)
                .map(([key, material]) => (
                  <MaterialChip
                    key={key}
                    matKey={key}
                    material={material}
                    isSelected={state.insertMaterial === key}
                    onSelect={() => actions.setInsertMaterial(key)}
                  />
                ))}
            </div>
          </Section>
        )}

        {/* Beam Parameters (Universal) */}
        <Section title="Beam Parameters" icon={Zap} defaultOpen={true}>
          <Slider
            label="Momentum"
            value={state.beam.momentum}
            min={0.1}
            max={5.0}
            step={0.1}
            unit="GeV/c"
            onChange={actions.setMomentum}
            tooltip="Beam momentum (p). Higher p = straighter tracks, deeper penetration."
          />
          <Slider
            label="Intensity"
            value={state.beam.intensity}
            min={1000}
            max={50000}
            step={1000}
            unit="p"
            onChange={actions.setBeamIntensity}
            tooltip="Number of particles to simulate."
          />
        </Section>

        {/* Geometry (Universal but adaptable) */}
        <Section title="Geometry" icon={Target} defaultOpen={true}>
          <Slider
            label={experimentType === 'medical' ? "Phantom Thickness" : "Target Thickness"}
            value={state.phantomThickness}
            min={0.5}
            max={20}
            step={0.5}
            unit="cm"
            onChange={actions.setPhantomThickness}
            tooltip="Thickness of the material interaction region."
          />
          {experimentType === 'decay' && (
             <Slider
                label="Decay Region"
                value={state.beamline.length}
                min={1}
                max={50}
                step={1}
                unit="m"
                onChange={actions.setBeamlineLength}
                tooltip="Length of the decay volume."
             />
          )}
        </Section>

        {/* Physics Settings (Conditional Toggles) */}
        <Section title="Physics" icon={Settings} defaultOpen={false}>
          {['decay', 'particle-id', 'cosmic'].includes(experimentType) && (
             <Toggle
                label="Decay Physics"
                value={state.physics.decayEnabled}
                onChange={actions.setDecayEnabled}
                tooltip="Enable unstable particle decay (π -> μ -> e)."
             />
          )}
          {['mcs-tomography', 'medical', 'calorimeter'].includes(experimentType) && (
             <Toggle
                label="Multiple Scattering"
                value={state.physics.mcsEnabled}
                onChange={actions.setMCSEnabled}
                tooltip="Enable Coulomb scattering off nuclei."
             />
          )}
          <Slider
            label="Magnetic Field"
            value={state.physics.magneticField || 0}
            min={0}
            max={5}
            step={0.1}
            unit="T"
            onChange={actions.setMagneticField}
            tooltip="B-field for bending charged particles."
          />
        </Section>

      </div>

      {/* Footer Actions */}
      <div className="p-3 bg-white border-t border-slate-200 space-y-2">
        {/* Progress */}
        <AnimatePresence>
          {isRunning && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-1"
            >
              <div className="flex justify-between text-xs font-medium text-[#0033A0]">
                <span>Simulating...</span>
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-[#0033A0] to-[#3498DB]"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results Button */}
        <AnimatePresence>
          {results && !isRunning && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Button
                variant="outline"
                className="w-full !border-emerald-500 !text-emerald-600 hover:!bg-emerald-50"
                icon={BarChart2}
                onClick={onShowResults}
              >
                View Analysis Results
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Run Button */}
        <Button
          variant="primary"
          className="w-full py-3 text-base shadow-md"
          icon={isRunning ? RefreshCw : Play}
          disabled={isRunning}
          onClick={runSimulation}
        >
          {isRunning ? 'Running...' : 'Run Simulation'}
        </Button>
      </div>
    </div>
  );
};

export default ControlPanel;